import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Ban, Calendar, CheckCircle2, FileText } from 'lucide-react';
import { useT } from '../../i18n';
import { api } from '../../api/client';
import { ProjectSection } from '../ProjectSection';
import { TranscriptModal } from '../TranscriptModal';
import { ACTIVITY_ICON, activityKind, formatProjectEventDescription, isTranscribing } from '../ProjectTimeline';
import { useStageName } from '../ProjectStageBar';
import type { TreeMilestone, TreeTask } from '../../proposals/vue-jalons-taches/api';
import { VueJalonsTaches } from '../../proposals/vue-jalons-taches/VueJalonsTaches';
import type { HomeMeeting, HomeProject } from '../../types';
import type { ExecutionHealth } from '../../utils/executionHealth';
import { LIFECYCLE_STAGES, RANK, lifecycleRank } from '../../utils/stages';
import { projectDisplayCode } from '../../utils/identifiers';
import { ProjectSpine } from './ProjectSpine';
import { HealthLabel } from './health';
import type { HomeProjectData } from './useHomeProject';
import { dayMonth, initials, isInProgressLabel, statusClass, whenLabel } from './homeFormat';
import { taskStatusName } from '../../utils/taskStatusName';

// One project I sponsor, drawn whole as the project page draws its parts: a card
// per subject. What it is about; its planning on one time axis; its milestones
// and tasks, the very view of the project page's Plan et exécution; who does
// what; its meetings as in the Chronologie (upcoming and past); the latest news
// on the activity line.

const ROLE_KEY: Record<string, string> = {
  contributor: 'roles.contributor', consulted: 'roles.consulted', accompanist: 'roles.accompanist', sponsor: 'roles.sponsor',
};
const SHOWN_PAST_MEETINGS = 3;
const SHOWN_UPCOMING_MEETINGS = 3;

/** The milestone marked on the planning at first: the one under way, else the next one, else the last. */
function defaultFocus(milestones: TreeMilestone[]): string | null {
  return (milestones.find(m => m.status === 'active') ?? milestones.find(m => m.status === 'pending') ?? milestones[milestones.length - 1])?.id ?? null;
}

const inProgressFirst = (a: TreeTask, b: TreeTask) =>
  (isInProgressLabel(a.column_label) ? 0 : 1) - (isInProgressLabel(b.column_label) ? 0 : 1)
  || (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999');

const pad = (n: number) => String(n).padStart(2, '0');
const clock = (iso: string) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

export function SponsoredProject({ project, health, data, loading, onChanged, onTasksChanged }: {
  project: HomeProject;
  health: ExecutionHealth | null;
  data: HomeProjectData;
  loading: boolean;
  onChanged: () => void;
  /** A task changed here: "Mes tâches", higher on the page, shows it too. */
  onTasksChanged: () => void;
}) {
  const { t } = useT();
  const stageName = useStageName();
  const { tree, detail } = data;
  // Per project, so coming back to one finds the milestone where it was left.
  const [focusBy, setFocusBy] = useState<Record<number, string>>({});
  // A click on the planning: that milestone unfolds and comes into view, once.
  const [focusRequest, setFocusRequest] = useState<{ projectId: number; milestoneId: string; at: number } | null>(null);
  useEffect(() => { setFocusRequest(null); }, [project.id]);
  const [goalOpen, setGoalOpen] = useState<number | null>(null);
  const [transcript, setTranscript] = useState<{ id: number; title: string } | null>(null);

  const milestones = tree.milestones;
  const focus = milestones.find(m => m.id === focusBy[project.id]) ?? milestones.find(m => m.id === defaultFocus(milestones)) ?? null;
  const setFocus = (id: string) => {
    setFocusBy(f => ({ ...f, [project.id]: id }));
    setFocusRequest({ projectId: project.id, milestoneId: id, at: Date.now() });
  };

  const openTasks = useMemo(() => tree.tasks.filter(x => !x.is_done), [tree]);
  const team = tree.team.filter(m => m.project_role !== 'sponsor');

  // Who does what: the team (the sponsor aside), plus anyone with open tasks who
  // isn't listed (the sponsor included, then under their sponsor role).
  const people = useMemo(() => {
    const list = team.map(m => ({ name: m.name, role: m.project_role as string | null, precision: m.precision }));
    const known = new Set(list.map(p => p.name.toLowerCase()));
    for (const task of openTasks) {
      for (const n of [task.assignee_name, ...(task.collaborator_names ?? [])]) {
        if (!n || known.has(n.toLowerCase())) continue;
        known.add(n.toLowerCase());
        const member = tree.team.find(m => m.name.toLowerCase() === n.toLowerCase());
        list.push({ name: n, role: member?.project_role ?? null, precision: member?.precision ?? '' });
      }
    }
    return list.map(p => {
      const lower = p.name.toLowerCase();
      const theirs = openTasks
        .filter(x => x.assignee_name?.toLowerCase() === lower || (x.collaborator_names ?? []).some(c => c.toLowerCase() === lower))
        .sort(inProgressFirst);
      return { ...p, tasks: theirs };
    }).sort((a, b) => b.tasks.length - a.tasks.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, openTasks]);

  const rank = lifecycleRank(project.status_label || '');
  const hasPlan = milestones.length > 0;
  const now = Date.now();
  const endOf = (m: HomeMeeting) => new Date(m.end_time || m.start_time).getTime();
  const upcoming = detail.meetings.filter(m => endOf(m) >= now).slice(0, SHOWN_UPCOMING_MEETINGS);
  const past = detail.meetings.filter(m => endOf(m) < now).reverse().slice(0, SHOWN_PAST_MEETINGS);
  const goal = detail.goal.trim();
  const longGoal = goal.length > 320;

  const refresh = () => { onChanged(); onTasksChanged(); };
  const setValidated = (deliverableId: string, validated: boolean) =>
    api.setDeliverableValidated(project.id, deliverableId, validated).then(onChanged).catch((e: Error) => alert(e.message));

  const meetingItem = (m: HomeMeeting, isPast: boolean) => {
    const tr = m.transcript;
    const info = [
      m.attendees.length ? m.attendees.join(', ') + (m.attendee_count > m.attendees.length ? ` +${m.attendee_count - m.attendees.length}` : '') : null,
      m.location,
    ].filter(Boolean) as string[];
    return (
      <div key={m.id} className={`meeting-item ${tr ? 'timeline-entry--combined' : 'timeline-entry--event'}`}>
        <div className="meeting-date">
          {dayMonth(new Date(m.start_time), t)}{!m.is_all_day && <> &middot; {clock(m.start_time)}&ndash;{clock(m.end_time)}</>}
        </div>
        <div className="meeting-subject"><Calendar size={12} className="timeline-icon" /> {m.subject || t('home.meetings.untitled')}</div>
        {info.length > 0 && (
          <div className="meeting-info">
            {info.map((part, i) => <span key={i}>{i > 0 && <span className="meeting-info-sep" />}{part}</span>)}
          </div>
        )}
        {isPast && (
          <div className="meeting-actions">
            {!tr ? (
              <span className="meeting-transcript-btn meeting-transcript-btn--disabled"><FileText size={12} className="timeline-icon" /> {t('timeline.noTranscript')}</span>
            ) : isTranscribing(tr.status) ? (
              <span className="meeting-transcript-btn meeting-transcript-btn--pending"><FileText size={12} className="timeline-icon" /> {t('timeline.transcribing')}</span>
            ) : /^failed$/i.test(tr.status || '') ? (
              <span className="meeting-transcript-btn meeting-transcript-btn--failed"><FileText size={12} className="timeline-icon" /> {t('timeline.transcriptFailed')}</span>
            ) : (
              <button type="button" className="meeting-transcript-btn meeting-transcript-btn--active" onClick={() => setTranscript({ id: tr.id, title: m.subject })}>
                <FileText size={12} className="timeline-icon" /> {t('timeline.viewTranscript')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`home-pdetail${loading ? ' is-loading' : ''}`} aria-busy={loading}>
      {/* What it is about */}
      <article className="pd-card home-phead">
        <div className="home-phead-main">
          <div className="home-phead-row">
            <span className="home-code">{projectDisplayCode(project)}</span>
            {project.status_label && <span className="badge badge-accent">{stageName(project.status_label)}</span>}
            {project.is_blocked && <span className="badge badge-err">{t('projectDetail.blockedBadge')}</span>}
            {health && <HealthLabel health={health} />}
            <span className="home-spacer" />
            <a className="btn btn-sm" href={`#project?id=${project.id}`}>{t('home.openProject')} <ArrowRight size={12} /></a>
          </div>
          <h3 className="home-phead-title">{project.name}</h3>
          {project.is_blocked && project.blocked_reason && (
            <p className="home-phead-blocked"><Ban size={13} aria-hidden="true" /><span>{project.blocked_reason}</span></p>
          )}
          {goal ? (
            <div className="home-goal">
              <p className={longGoal && goalOpen !== project.id ? 'is-clamped' : undefined}>{goal}</p>
              {longGoal && (
                <button type="button" className="home-link-btn" onClick={() => setGoalOpen(v => (v === project.id ? null : project.id))}>
                  {goalOpen === project.id ? t('home.goalLess') : t('home.goalMore')}
                </button>
              )}
            </div>
          ) : (
            <p className="home-goal home-muted">{t('home.noGoal')}</p>
          )}
        </div>
        <dl className="home-facts">
          <div className="home-fact"><dt>{t('roles.sponsor')}</dt><dd>{t('home.you')}</dd></div>
          <div className="home-fact"><dt>{t('roles.accompanist')}</dt><dd>{detail.pmo_accompanist || '—'}</dd></div>
          <div className="home-fact home-fact--team">
            <dt>{t('home.team')}</dt>
            <dd>
              {team.length === 0 ? '—' : (
                <span className="home-tags">
                  {team.slice(0, 10).map(m => <span key={m.name} className="home-tag" title={m.name}>{initials(m.name)}</span>)}
                  {team.length > 10 && <span className="home-tag home-tag--more">+{team.length - 10}</span>}
                </span>
              )}
            </dd>
          </div>
          <div className="home-fact">
            <dt>{t('home.budget')}</dt>
            <dd className={detail.budget.available || detail.budget.requested ? 'home-mono' : undefined}>
              {detail.budget.available
                ? t('home.budgetBoth', { available: detail.budget.available, requested: detail.budget.requested || '—' })
                : detail.budget.requested || '—'}
            </dd>
          </div>
        </dl>
      </article>

      {/* Planning, meetings and activity on one axis — or, before Planning, where the project stands */}
      {hasPlan ? (
        <article className="pd-card">
          <div className="pd-card-head">
            <h4 className="pd-card-title">{t('home.spine.title')}</h4>
          </div>
          <div className="pd-card-body home-spine-body">
            <ProjectSpine projectId={project.id} milestones={milestones} tasks={tree.tasks} meetings={detail.meetings}
              tasksDone={detail.tasks_done} focusId={focus?.id ?? null} onFocus={setFocus} />
          </div>
        </article>
      ) : (
        <article className="pd-card">
          <div className="pd-card-head">
            <h4 className="pd-card-title">{t('home.stages.title')}</h4>
            {rank < RANK.planning && <span className="home-meta">{t('home.stages.noPlanYet')}</span>}
          </div>
          <div className="pd-card-body">
            <ol className="home-stepper">
              {LIFECYCLE_STAGES.slice(0, RANK.closed).map((s, i) => (
                <li key={s} className={i < rank ? 'is-done' : i === rank ? 'is-current' : undefined}>
                  <span className="home-stepper-num">{i < rank ? <CheckCircle2 size={12} /> : i + 1}</span>
                  <span className="home-stepper-label">{stageName(s)}</span>
                </li>
              ))}
            </ol>
          </div>
        </article>
      )}

      {/* Its milestones and tasks, as on the project page (Plan et exécution) */}
      {hasPlan && (
        <ProjectSection title={t('projectDetail.topics.milestonesAndTasks')} flush>
          <VueJalonsTaches key={project.id} projectId={project.id} tree={tree} onChanged={refresh} onDetailChanged={refresh}
            validation={{ validations: detail.validations, canValidate: detail.can_validate, onSet: setValidated }}
            focusRequest={focusRequest?.projectId === project.id ? focusRequest : null} />
        </ProjectSection>
      )}

      <div className="home-pgrid">
        <div className="home-pgrid-col home-pgrid-col--main">
          {/* Latest news, on the Chronologie's activity line */}
          <article className="pd-card">
            <div className="pd-card-head">
              <h4 className="pd-card-title">{t('home.news.title')}</h4>
              <a className="home-more-link" href={`#project?id=${project.id}`}>{t('home.news.all')} <ArrowRight size={12} /></a>
            </div>
            <div className="pd-card-body home-feed">
              {detail.news.length === 0 && <p className="home-muted">{t('home.news.none')}</p>}
              {detail.news.map(item => {
                if (item.kind === 'event') {
                  const kind = activityKind(item.event.event_type);
                  const Icon = ACTIVITY_ICON[kind];
                  return (
                    <div key={`e-${item.event.id}`} className="meeting-item timeline-entry--task_event">
                      <div className={`tl-act tl-act--${kind}`}>
                        <span className="tl-act-icon" aria-hidden><Icon size={12} /></span>
                        <div className="tl-act-body">
                          <span className="tl-act-text">{formatProjectEventDescription(item.event, t)}</span>
                          <span className="tl-act-meta">{item.event.actor_name ? `${item.event.actor_name} · ` : ''}{whenLabel(item.at, t)}</span>
                        </div>
                      </div>
                    </div>
                  );
                }
                const c = item.comment;
                return (
                  <div key={`c-${c.id}`} className="meeting-item timeline-entry--comment">
                    <span className="tl-avatar" aria-hidden>{initials(c.by || '')}</span>
                    <div className="timeline-comment-header">
                      <span className="task-update-author">{c.by}</span>
                      <span className="task-update-time">
                        {c.task_title
                          ? t(c.reply ? 'home.news.replyOnTask' : 'home.news.commentOnTask', { task: c.task_title })
                          : t(c.reply ? 'home.news.reply' : 'home.news.comment')}
                        {' · '}{whenLabel(item.at, t)}
                      </span>
                    </div>
                    {c.excerpt && <div className="task-update-content">{c.excerpt.length > 160 ? `${c.excerpt.slice(0, 160)}…` : c.excerpt}</div>}
                  </div>
                );
              })}
            </div>
          </article>
        </div>

        <div className="home-pgrid-col">
          {/* Who does what */}
          <article className="pd-card">
            <div className="pd-card-head">
              <h4 className="pd-card-title">{t('home.who.title')}</h4>
              {people.length > 0 && <span className="home-meta">{t(people.length > 1 ? 'home.who.peopleMany' : 'home.who.peopleOne', { n: people.length })}</span>}
            </div>
            <div className="pd-card-body home-people">
              {people.length === 0 && <p className="home-muted">{t('home.who.empty')}</p>}
              {people.map(p => (
                <div key={p.name} className="home-person">
                  <span className="home-tag home-tag--lg" aria-hidden="true">{initials(p.name)}</span>
                  <div className="home-person-main">
                    <div className="home-person-name">
                      <span>{p.name}</span>
                      {p.tasks.length > 0 && <span className="home-count" title={t(p.tasks.length > 1 ? 'home.who.openMany' : 'home.who.openOne', { n: p.tasks.length })}>{p.tasks.length}</span>}
                    </div>
                    <div className="home-meta">
                      {[p.role ? t(ROLE_KEY[p.role] ?? 'roles.contributor') : null, p.precision || null].filter(Boolean).join(' · ') || t('home.who.noRole')}
                    </div>
                    {p.tasks.length === 0 ? (
                      <div className="home-person-idle">{t('home.who.nothing')}</div>
                    ) : (
                      <ul className="home-person-tasks">
                        {p.tasks.slice(0, 3).map(x => (
                          <li key={x.id}>
                            <i className={`home-dot ${statusClass(x.column_label)}`} title={taskStatusName(x.column_label, t)} aria-hidden="true" />
                            {x.can_open ? <a href={`#tasks?task=${x.id}`}>{x.title}</a> : <span className="home-muted">{t('home.who.privateTask')}</span>}
                          </li>
                        ))}
                        {p.tasks.length > 3 && <li className="home-meta">{t('home.who.more', { n: p.tasks.length - 3 })}</li>}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </article>

          {/* Meetings, as in the Chronologie */}
          <article className="pd-card">
            <div className="pd-card-head">
              <h4 className="pd-card-title">{t('home.meetings.title')}</h4>
              <a className="home-more-link" href={`#project?id=${project.id}`}>{t('home.meetings.all')} <ArrowRight size={12} /></a>
            </div>
            <div className="pd-card-body home-meetings">
              {upcoming.length === 0 && past.length === 0 && <p className="home-muted">{t('home.meetings.none')}</p>}
              {upcoming.length > 0 && (
                <div className="meetings-group">
                  <div className="meetings-group-label">{t('timeline.upcoming')}</div>
                  {upcoming.map(m => meetingItem(m, false))}
                </div>
              )}
              {upcoming.length === 0 && past.length > 0 && <p className="home-muted home-meetings-none">{t('home.meetings.noneUpcoming')}</p>}
              {past.length > 0 && (
                <div className="meetings-group">
                  <div className="meetings-group-label">{t('timeline.past')}</div>
                  {past.map(m => meetingItem(m, true))}
                </div>
              )}
            </div>
          </article>
        </div>
      </div>

      {transcript && <TranscriptModal transcriptionId={transcript.id} title={transcript.title} onClose={() => setTranscript(null)} />}
    </div>
  );
}
